import { zodResolver } from "@hookform/resolvers/zod";
import { Trash01, UploadCloud02 } from "@untitledui/icons";
import { type DragEvent, useRef, useState } from "react";
import { Controller, type UseFormReturn, useForm } from "react-hook-form";
import z from "zod/v3";
import {
  type MerchantDraftItem,
  useKeepFoundLogo,
  useLogoLookup,
  useLogoSearch,
  useRemoveDraftLogo,
  useUploadDraftLogo,
} from "@/api/workspace-merchants";
import { Button } from "@/components/base/buttons/button";
import { useDebounce } from "@/hooks/use-debounce";
import { cx } from "@/utils/cx";
import { LOGO_MAX_BYTES, logoImageUrl } from "@/utils/logo-dev";
import { FieldRow, TextInput } from "./wizard-ui";

const detailsSchema = z.object({
  handle: z.string().trim().min(1, "Enter the shop's TikTok handle"),
  name: z.string().trim().min(1, "Enter the merchant name"),
});

export type DetailsValue = z.infer<typeof detailsSchema>;

export const useDetailsForm = (defaultValues: DetailsValue) =>
  useForm<DetailsValue>({ resolver: zodResolver(detailsSchema), defaultValues, mode: "onChange" });

const FieldError = ({ message }: { message?: string }) =>
  message ? <p className="text-sm text-warning-600">{message}</p> : null;

const LOGO_TYPES = ["image/svg+xml", "image/png", "image/jpeg", "image/gif", "image/webp"];

export const isDetailsComplete = (value: DetailsValue) => !!value.handle.trim() && !!value.name.trim();

type Candidate = { name: string; domain: string };

const useLogoCandidates = (query: string) => {
  const debounced = useDebounce(query.trim(), 400);
  const looksLikeWebsite = debounced.includes(".");
  const search = useLogoSearch(looksLikeWebsite ? "" : debounced);
  const lookup = useLogoLookup(looksLikeWebsite ? debounced : "");

  if (!debounced) return { candidates: [] as Candidate[], isLoading: false, searched: false };
  if (looksLikeWebsite) {
    const data = lookup.data;
    const candidates = data?.ok && data.domain ? [{ name: data.domain, domain: data.domain }] : [];
    return { candidates, isLoading: lookup.isFetching, searched: !!data };
  }
  return { candidates: search.data?.results ?? [], isLoading: search.isFetching, searched: !!search.data };
};

const LogoPreview = ({
  draft,
  onRemove,
  isRemoving,
}: {
  draft: MerchantDraftItem;
  onRemove: () => void;
  isRemoving: boolean;
}) => (
  <div className="flex items-center gap-4 rounded-xl border border-secondary bg-white p-4">
    {draft.logoUrl ? (
      <img src={draft.logoUrl} alt="Merchant logo" className="h-12 max-w-[120px] rounded-md object-contain" />
    ) : (
      <span className="h-12 w-12 rounded-md bg-gray-100" />
    )}
    <div className="flex flex-1 flex-col min-w-0">
      <p className="text-sm font-semibold text-primary truncate">
        {draft.data.logo?.source === "found" ? draft.data.logo.domain : "Uploaded logo"}
      </p>
      <p className="text-sm text-tertiary">
        {draft.data.logo?.source === "found" ? "Found from your website" : "Uploaded by you"}
      </p>
    </div>
    <Button color="tertiary" size="sm" iconLeading={Trash01} onClick={onRemove} isLoading={isRemoving}>
      Remove
    </Button>
  </div>
);

const LogoPicker = () => {
  const [query, setQuery] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const { candidates: found, isLoading, searched } = useLogoCandidates(query);
  const [missingLogos, setMissingLogos] = useState<Set<string>>(() => new Set());
  const candidates = found.filter((candidate) => !missingLogos.has(candidate.domain));
  const hideCandidate = (domain: string) => setMissingLogos((current) => new Set(current).add(domain));
  const keepFound = useKeepFoundLogo();
  const upload = useUploadDraftLogo();

  const uploadFile = (file: File | undefined) => {
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) {
      setUploadError("Upload a SVG, PNG, JPG or GIF image.");
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setUploadError("Upload an image under 2MB.");
      return;
    }
    setUploadError(null);
    upload.mutate(file);
  };

  const onDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragging(false);
    uploadFile(event.dataTransfer.files[0]);
  };

  return (
    <>
      <TextInput
        id="merchant-logo"
        placeholder="Find your brand's logo or enter your website"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {query.trim() && (
        <div className="flex flex-col gap-2">
          {isLoading && <p className="text-sm text-tertiary">Looking for logos…</p>}
          {!isLoading && searched && candidates.length === 0 && (
            <p className="text-sm text-tertiary">
              We couldn't find a logo. Try your brand's website, or upload your own.
            </p>
          )}
          {candidates.length > 0 && (
            <ul className="flex flex-col rounded-xl border border-secondary bg-white">
              {candidates.map((candidate) => (
                <li key={candidate.domain} className="border-b border-secondary last:border-b-0">
                  <button
                    type="button"
                    disabled={keepFound.isPending}
                    onClick={() => keepFound.mutate(candidate.domain)}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-primary_hover"
                  >
                    <img
                      src={logoImageUrl(candidate.domain)}
                      alt=""
                      className="h-8 w-16 rounded object-contain"
                      onError={() => hideCandidate(candidate.domain)}
                    />
                    <span className="flex flex-1 flex-col min-w-0">
                      <span className="text-sm font-medium text-primary truncate">{candidate.name}</span>
                      <span className="text-xs text-tertiary truncate">{candidate.domain}</span>
                    </span>
                    <span className="text-sm font-semibold text-brand-700">Use this logo</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {keepFound.error && <p className="text-sm text-warning-600">{keepFound.error.message}</p>}
          {candidates.length > 0 && (
            <p className="text-xs text-quaternary">
              Logos provided by{" "}
              <a href="https://www.logo.dev" target="_blank" rel="noreferrer" className="underline">
                Logo.dev
              </a>
            </p>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 text-sm text-tertiary">
        <span className="h-px flex-1 bg-gray-200" />
        or upload your own
        <span className="h-px flex-1 bg-gray-200" />
      </div>

      <button
        type="button"
        onClick={() => fileInput.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        className={cx(
          "flex flex-col items-center gap-3 rounded-xl border border-dashed bg-white px-6 py-5 transition-colors",
          isDragging ? "border-brand-500 bg-brand-25" : "border-primary",
        )}
      >
        <span className="flex size-10 items-center justify-center rounded-lg border border-secondary shadow-xs">
          <UploadCloud02 className="size-5 text-secondary" />
        </span>
        <span className="text-sm text-tertiary">
          <span className="font-semibold text-brand-700">{upload.isPending ? "Uploading…" : "Click to upload"}</span> or
          drag and drop
        </span>
        <span className="text-xs text-tertiary">Max 800 × 400px · SVG, PNG, JPG or GIF · under 2MB</span>
      </button>
      <input
        ref={fileInput}
        type="file"
        accept={LOGO_TYPES.join(",")}
        className="hidden"
        aria-label="Upload logo"
        onChange={(event) => {
          uploadFile(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
      {uploadError && <p className="text-sm text-warning-600">{uploadError}</p>}
    </>
  );
};

export const StepDetails = ({ form, draft }: { form: UseFormReturn<DetailsValue>; draft: MerchantDraftItem }) => {
  const removeLogo = useRemoveDraftLogo();
  const { errors } = form.formState;

  return (
    <div className="rounded-2xl border border-secondary bg-white px-8">
      <FieldRow
        label="TikTok Shop handle"
        required
        htmlFor="merchant-handle"
        description="Your shop's @handle on TikTok. It links your advertising data to this account."
      >
        <Controller
          control={form.control}
          name="handle"
          render={({ field }) => (
            <div
              className={cx(
                "flex h-10 overflow-hidden rounded-lg border bg-white shadow-xs focus-within:border-brand-500 focus-within:ring-4 focus-within:ring-brand-100",
                errors.handle ? "border-warning-400" : "border-primary",
              )}
            >
              <span className="flex items-center border-r border-primary bg-gray-50 px-3 text-md text-tertiary">@</span>
              <input
                id="merchant-handle"
                placeholder="yourbrand"
                aria-invalid={!!errors.handle || undefined}
                value={field.value}
                onBlur={field.onBlur}
                onChange={(event) => field.onChange(event.target.value.replace(/^@/, ""))}
                className="flex-1 px-3.5 text-md text-primary outline-none placeholder:text-placeholder"
              />
            </div>
          )}
        />
        <FieldError message={errors.handle?.message} />
      </FieldRow>

      <FieldRow
        label="Preferred merchant name"
        required
        htmlFor="merchant-name"
        description="How your account appears in Unbox."
      >
        <TextInput
          id="merchant-name"
          placeholder="e.g. Acme Foods"
          isInvalid={!!errors.name}
          {...form.register("name")}
        />
        <FieldError message={errors.name?.message} />
      </FieldRow>

      <FieldRow
        label="Logo"
        htmlFor="merchant-logo"
        description="Shown on your Unbox account. Search for your brand and we'll fetch it, or upload your own."
      >
        {draft.data.logo ? (
          <LogoPreview draft={draft} onRemove={() => removeLogo.mutate()} isRemoving={removeLogo.isPending} />
        ) : (
          <LogoPicker />
        )}
      </FieldRow>
    </div>
  );
};
